import { useEffect, useMemo, useRef, useState } from "react";
import "./PurchaseV1.css";

const overviewStats = [
  ["Purchased this month", "₹0", "No bills yet"],
  ["Supplier payable", "₹0", "No pending bills"],
  ["Input GST", "₹0", "No eligible ITC yet"],
  ["Needs review", "0", "No OCR or tax checks"]
];

const recentPurchases = [];

const purchaseLines = [];

const knownSuppliers = [];

const drafts = [];

const history = [];

const fieldGroups = {
  frame: [
    ["Gender", ""],
    ["Colour", ""],
    ["Frame type", ""],
    ["Shape", ""],
    ["Material", ""],
    ["Size / eye size", ""],
    ["Bridge size", ""],
    ["Temple length", ""],
    ["Temple detail", ""]
  ],
  lens: [
    ["Lens category", ""],
    ["Material / index", ""],
    ["Coating", ""],
    ["Design / corridor", ""],
    ["Diameter", ""],
    ["Supply mode", ""],
    ["Sphere", ""],
    ["Cylinder", ""],
    ["Addition", ""]
  ],
  contact: [
    ["Modality", ""],
    ["Pack size", ""],
    ["Boxes purchased", ""],
    ["Base curve", ""],
    ["Diameter", ""],
    ["Colour", ""],
    ["Sphere", ""],
    ["Cylinder", ""],
    ["Axis / Add", ""],
    ["Batch number", ""],
    ["Manufactured date", ""],
    ["Expiry date", ""]
  ],
  solution: [
    ["Volume", ""],
    ["Pack configuration", ""],
    ["Batch number", ""],
    ["Manufactured date", ""],
    ["Expiry date", ""]
  ],
  service: [
    ["Expense category", ""],
    ["Service period from", ""],
    ["Service period to", ""]
  ],
  asset: [
    ["Asset category", ""],
    ["Serial number", ""],
    ["Warranty expiry", ""]
  ]
};

const categoryLabels = [
  ["frame", "Frame"],
  ["lens", "Lens"],
  ["sunglasses", "Sunglasses"],
  ["contact", "Contact Lens"],
  ["solution", "Solution"],
  ["accessory", "Case / Accessory"],
  ["service", "Service"],
  ["asset", "Asset"],
  ["other", "Other"],
  ["nonchargeable", "Non-chargeable"]
];

function Badge({ children, tone = "" }) {
  return <span className={`pv1-badge ${tone}`}>{children}</span>;
}

function Field({ label, children }) {
  return (
    <label className="pv1-field">
      <span>{label}</span>
      {children || <input />}
    </label>
  );
}

function PurchaseRow({ row }) {
  return (
    <div className="pv1-purchase-row">
      <div>
        <strong>{row[0]}</strong>
        <span>{row[1]}</span>
      </div>
      <div className="pv1-amount">
        <strong>{row[2]}</strong>
        <Badge tone={row[3] === "Paid" ? "" : row[3] === "Part paid" ? "blue" : "amber"}>{row[3]}</Badge>
      </div>
    </div>
  );
}

function EmptyState({ title, note }) {
  return (
    <div className="pv1-empty">
      <div>🧾</div>
      <strong>{title}</strong>
      <span>{note}</span>
    </div>
  );
}

const emptyExtractedInvoice = {
  supplierName: "",
  billNumber: "",
  billDate: "",
  gstin: "",
  taxableAmount: "",
  gstAmount: "",
  totalAmount: "",
  vendorReference: ""
};

const emptyBillDraft = {
  supplierName: "",
  supplierGstin: "",
  billNumber: "",
  billDate: "",
  invoiceType: "",
  placeOfSupply: "",
  taxRule: "",
  taxType: "",
  receivingBusiness: "",
  receivingStore: "",
  ewayBill: "",
  irn: ""
};

const emptyNewSupplier = {
  name: "",
  gstin: "",
  phone: "",
  city: ""
};

function normaliseAmount(value = "") {
  return value.replace(/,/g, "").trim();
}

function normaliseInvoiceDate(value = "") {
  const match = value.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
  if (!match) return "";
  const [, day, month, year] = match;
  const fullYear = year.length === 2 ? `20${year}` : year;
  return `${fullYear.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

function extractInvoiceFields(text = "") {
  const cleanedText = text.replace(/\s+/g, " ").trim();
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const gstin = cleanedText.match(/\b[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]\b/i)?.[0]?.toUpperCase() || "";
  const billNumber = cleanedText.match(/\b(?:invoice|inv|bill|challan|dc|voucher)\s*(?:no|number|#)?\s*[:\-]?\s*([A-Z0-9][A-Z0-9/\-.]{2,})/i)?.[1] || "";
  const billDate = cleanedText.match(/\b(\d{1,2}[\/\-.]\d{1,2}[\/\-.]\d{2,4})\b/)?.[1] || "";
  const vendorReference = cleanedText.match(/\b(?:lrn|lrf|dc|dn|challan|reference|ref)\s*(?:no|number|#)?\s*[:\-]?\s*([A-Z0-9][A-Z0-9/\-.]{2,})/i)?.[1] || "";
  const taxableAmount = normaliseAmount(cleanedText.match(/\b(?:taxable value|taxable amount|assessable value|basic amount)\s*[:\-]?\s*(?:₹|rs\.?|inr)?\s*([0-9,]+(?:\.\d{1,2})?)/i)?.[1] || "");
  const gstAmount = normaliseAmount(cleanedText.match(/\b(?:total gst|gst amount|igst|cgst|sgst)\s*[:\-]?\s*(?:₹|rs\.?|inr)?\s*([0-9,]+(?:\.\d{1,2})?)/i)?.[1] || "");
  const labelledTotal = normaliseAmount(cleanedText.match(/\b(?:grand total|invoice total|total amount|net amount|amount payable|total)\s*[:\-]?\s*(?:₹|rs\.?|inr)?\s*([0-9,]+(?:\.\d{1,2})?)/i)?.[1] || "");
  const detectedAmounts = [...cleanedText.matchAll(/(?:₹|rs\.?|inr)?\s*([0-9]{1,3}(?:,[0-9]{3})+(?:\.\d{1,2})|[0-9]+(?:\.\d{1,2}))/gi)]
    .map((match) => Number(normaliseAmount(match[1])))
    .filter((amount) => Number.isFinite(amount) && amount > 0);
  const totalAmount = labelledTotal || (detectedAmounts.length ? String(Math.max(...detectedAmounts)) : "");
  const supplierName = lines.find((line) => /(opt|lens|vision|care|trader|supplier|company|pvt|ltd|enterprises|agency)/i.test(line)) || lines[0] || "";

  return {
    supplierName,
    billNumber,
    billDate: normaliseInvoiceDate(billDate),
    gstin,
    taxableAmount,
    gstAmount,
    totalAmount,
    vendorReference
  };
}

function findSupplierMatch(extracted = {}) {
  const gstin = extracted.gstin?.trim().toUpperCase();
  const name = extracted.supplierName?.trim().toLowerCase();
  return knownSuppliers.find((supplier) => {
    if (gstin && supplier.gstin.toUpperCase() === gstin) return true;
    if (!name) return false;
    return supplier.name.toLowerCase().includes(name) || name.includes(supplier.name.toLowerCase());
  }) || null;
}

function guessProductType(line = "") {
  if (/contact/i.test(line)) return "Contact Lens";
  if (/solution|cleaner|spray|ml\b/i.test(line)) return "Solution";
  if (/sun|goggle/i.test(line)) return "Sunglasses";
  if (/lens|index|coat|blue|progressive|single vision|bifocal/i.test(line)) return "Lens";
  if (/frame|rim|metal|acetate|tr90/i.test(line)) return "Frame";
  return "Other";
}

function extractInvoiceLineCandidates(text = "") {
  const blockedWords = /(invoice|bill no|gstin|grand total|total amount|taxable value|cgst|sgst|igst|round off|address|phone|email|bank|upi)/i;
  return text
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter((line) => line.length >= 8 && /[a-z]/i.test(line) && /\d/.test(line) && !blockedWords.test(line))
    .map((line, index) => {
      const numbers = [...line.matchAll(/([0-9]+(?:\.[0-9]{1,2})?)/g)].map((match) => Number(match[1])).filter(Number.isFinite);
      const total = numbers.length ? numbers[numbers.length - 1] : "";
      const qty = numbers.find((value) => Number.isInteger(value) && value > 0 && value <= 999) || 1;
      const rate = numbers.length > 1 ? numbers[numbers.length - 2] : "";
      const hsn = line.match(/\b([0-9]{4,8})\b/)?.[1] || "";
      const productDetails = line.replace(/(?:₹|rs\.?|inr)/gi, "").replace(/\s{2,}/g, " ").trim();
      return {
        id: `ocr-line-${Date.now()}-${index}`,
        productType: guessProductType(line),
        productDetails,
        hsn,
        qty: String(qty),
        rate: rate ? String(rate) : "",
        total: total ? String(total) : "",
        status: "review"
      };
    })
    .slice(0, 12);
}

function ProductDetailSheet({ open, onClose }) {
  const [category, setCategory] = useState("");
  if (!open) return null;

  const visibleGroups = useMemo(() => {
    if (category === "sunglasses") return [["Frame details", fieldGroups.frame]];
    if (category === "lens") return [["Lens details", fieldGroups.lens]];
    if (category === "contact") return [["Contact-lens details", fieldGroups.contact]];
    if (category === "solution") return [["Solution details", fieldGroups.solution]];
    if (category === "service") return [["Service details", fieldGroups.service]];
    if (category === "asset") return [["Asset details", fieldGroups.asset]];
    return [];
  }, [category]);

  return (
    <div className="pv1-modal" role="dialog" aria-modal="true">
      <button className="pv1-modal-scrim" type="button" aria-label="Close product details" onClick={onClose} />
      <section className="pv1-sheet pv1-product-sheet">
        <div className="pv1-handle" />
        <div className="pv1-sheet-head">
          <h2>Product details</h2>
          <button className="pv1-close" type="button" onClick={onClose}>×</button>
        </div>

        <div className="pv1-type-chips">
          {categoryLabels.map(([key, label]) => (
            <button key={key} className={category === key ? "active" : ""} type="button" onClick={() => setCategory(key)}>
              {label}
            </button>
          ))}
        </div>

        <div className="pv1-quick-row">
          <button type="button">📷 Extract product label</button>
          <button type="button">▦ Scan barcode</button>
          <button type="button">↺ Use last purchase value</button>
        </div>

        <p className="pv1-section-label">Product identity</p>
        <div className="pv1-search">
          <span>⌕</span>
          <input placeholder="Search existing product, model or barcode" />
          <button type="button">Select</button>
        </div>
        <div className="pv1-field-grid cols3">
          <Field label="Product code"><input placeholder="Optional" /></Field>
          <Field label="Product name *"><input placeholder="Product name" /></Field>
          <Field label="Brand"><input placeholder="Brand" /></Field>
          <Field label="Model / article"><input placeholder="Model / article" /></Field>
          <Field label="Supplier product code"><input placeholder="If different" /></Field>
          <Field label="Quality / collection"><input placeholder="Optional" /></Field>
        </div>

        {visibleGroups.map(([title, fields]) => (
          <div key={title}>
            <p className="pv1-section-label">{title}</p>
            <div className="pv1-field-grid cols3">
              {fields.map(([label, value]) => (
                <Field key={label} label={label}><input defaultValue={value} /></Field>
              ))}
            </div>
          </div>
        ))}
        {!category && <EmptyState title="Select product type" note="Product-specific fields will open after choosing Frame, Lens, Contact Lens, Solution, Service or Asset." />}

        <p className="pv1-section-label">Quantity and pricing</p>
        <div className="pv1-field-grid cols3">
          <Field label="Unit"><select defaultValue=""><option value="">Select unit</option><option>PCS</option><option>PAIR</option><option>BOX</option><option>PACK</option><option>JOB</option><option>MONTH</option></select></Field>
          <Field label="Quantity *"><input type="number" placeholder="Qty" /></Field>
          <Field label="Purchase price per unit *"><input placeholder="0" /></Field>
          <Field label="Discount per unit ₹"><input placeholder="0" /></Field>
          <Field label="Discount %"><input placeholder="0" /></Field>
          <Field label="MRP / retail price"><input placeholder="0" /></Field>
        </div>

        <p className="pv1-section-label">GST</p>
        <div className="pv1-field-grid cols3">
          <Field label={category === "service" ? "SAC code *" : "HSN code *"}><input placeholder={category === "service" ? "SAC code" : "HSN code"} /></Field>
          <Field label="GST slab *"><select defaultValue=""><option value="">Select GST</option><option>Nil rated</option><option>Exempt</option><option>0%</option><option>5%</option><option>12%</option><option>18%</option><option>28%</option></select></Field>
          <Field label="Price tax rule"><select defaultValue=""><option value="">Select tax rule</option><option>Tax included</option><option>Tax excluded</option><option>Not applicable</option></select></Field>
          <Field label="Tax applied as"><select defaultValue=""><option value="">Select tax type</option><option>CGST 6% + SGST 6%</option><option>IGST 12%</option><option>No GST</option></select></Field>
          <Field label="ITC eligibility"><select defaultValue=""><option value="">Select ITC</option><option>Eligible</option><option>Partly eligible</option><option>Ineligible / blocked</option><option>Pending verification</option></select></Field>
          <Field label="Reverse charge / cess"><select defaultValue=""><option value="">Select</option><option>Not applicable</option><option>Reverse charge</option><option>Cess applicable</option></select></Field>
        </div>

        <p className="pv1-section-label">Inventory and barcode</p>
        <div className="pv1-field-grid cols3">
          <Field label="Track inventory"><div className="pv1-toggle"><button type="button">Yes</button><button type="button">No</button></div></Field>
          <Field label="Allow negative inventory"><div className="pv1-toggle"><button type="button">Yes</button><button type="button">No</button></div></Field>
          <Field label="Stock status"><select defaultValue=""><option value="">Select status</option><option>Available</option><option>Hold for checking</option><option>Damaged</option></select></Field>
          <Field label="Receiving quantity"><input placeholder="Auto from quantity" readOnly /></Field>
          <Field label="Minimum stock alert"><input placeholder="Optional" /></Field>
        </div>

        <p className="pv1-section-label">Barcode method</p>
        <div className="pv1-barcode-modes">
          <button type="button"><strong>Unique barcode per piece</strong><span>Quantity 6 creates six separately traceable barcodes.</span></button>
          <button type="button"><strong>Same barcode for all pieces</strong><span>Use when every piece is the same item or SKU.</span></button>
          <button type="button"><strong>Use supplier barcode</strong><span>Scan or enter the barcode already on the product.</span></button>
          <button type="button"><strong>No barcode</strong><span>For non-tracked items where a label is unnecessary.</span></button>
        </div>
        <div className="pv1-barcode-preview">
          <strong>Preview</strong>
          <div>
            <span>Barcode will appear after product details are entered</span>
          </div>
        </div>

        <p className="pv1-section-label">Calculated values</p>
        <div className="pv1-field-grid cols3">
          <Field label="Net unit / base price"><input placeholder="₹0.00" readOnly /></Field>
          <Field label="GST per unit"><input placeholder="₹0.00" readOnly /></Field>
          <Field label="Line total"><input placeholder="₹0.00" readOnly /></Field>
        </div>
        <button className="pv1-primary full" type="button" onClick={onClose}>Add to Purchase</button>
      </section>
    </div>
  );
}

export default function PurchaseV1() {
  const [view, setView] = useState("overview");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [payment, setPayment] = useState("");
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoicePreviewUrl, setInvoicePreviewUrl] = useState("");
  const [supplierMode, setSupplierMode] = useState("");
  const [ocrStatus, setOcrStatus] = useState("idle");
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrError, setOcrError] = useState("");
  const [extractedText, setExtractedText] = useState("");
  const [extractedInvoice, setExtractedInvoice] = useState(emptyExtractedInvoice);
  const [billDraft, setBillDraft] = useState(emptyBillDraft);
  const [newSupplierDraft, setNewSupplierDraft] = useState(emptyNewSupplier);
  const [invoiceLineDrafts, setInvoiceLineDrafts] = useState([]);
  const [supplierMatch, setSupplierMatch] = useState(null);
  const uploadInvoiceInputRef = useRef(null);

  const openPurchase = () => setView("newPurchase");
  const invoiceStatusText = invoiceFile ? invoiceFile.name : "No invoice uploaded";
  const isInvoiceImage = invoiceFile?.type?.startsWith("image/");
  const isInvoicePdf = invoiceFile?.type === "application/pdf";
  const approvedLineCount = invoiceLineDrafts.filter((line) => line.status === "approved").length;
  const draftQuantity = invoiceLineDrafts.reduce((sum, line) => sum + (Number(line.qty) || 0), 0);
  const draftGrandTotal = extractedInvoice.totalAmount || "0";
  const draftTaxableAmount = extractedInvoice.taxableAmount || "0";
  const draftGstAmount = extractedInvoice.gstAmount || "0";

  useEffect(() => {
    if (!invoiceFile || !isInvoiceImage) {
      setInvoicePreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(invoiceFile);
    setInvoicePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [invoiceFile, isInvoiceImage]);

  const handleInvoiceFile = (event) => {
    const [file] = Array.from(event.target.files || []);
    if (!file) return;
    setInvoiceFile(file);
    setOcrStatus("idle");
    setOcrProgress(0);
    setOcrError("");
    setExtractedText("");
    setExtractedInvoice(emptyExtractedInvoice);
    setInvoiceLineDrafts([]);
    setSupplierMatch(null);
    event.target.value = "";
  };

  const updateExtractedInvoice = (field, value) => {
    setExtractedInvoice((current) => ({ ...current, [field]: value }));
  };

  const updateBillDraft = (field, value) => {
    setBillDraft((current) => ({ ...current, [field]: value }));
  };

  const updateNewSupplierDraft = (field, value) => {
    setNewSupplierDraft((current) => ({ ...current, [field]: value }));
  };

  const updateInvoiceLineDraft = (id, field, value) => {
    setInvoiceLineDrafts((current) => current.map((line) => (line.id === id ? { ...line, [field]: value } : line)));
  };

  const removeInvoiceLineDraft = (id) => {
    setInvoiceLineDrafts((current) => current.filter((line) => line.id !== id));
  };

  const approveInvoiceLineDraft = (id) => {
    setInvoiceLineDrafts((current) => current.map((line) => (line.id === id ? { ...line, status: "approved" } : line)));
  };

  const applyExtractedInvoiceToDraft = () => {
    const matchedSupplier = findSupplierMatch(extractedInvoice);
    setSupplierMatch(matchedSupplier);
    if (matchedSupplier) {
      setSupplierMode(matchedSupplier.name);
      setBillDraft((current) => ({
        ...current,
        supplierName: matchedSupplier.name,
        supplierGstin: matchedSupplier.gstin,
        billNumber: extractedInvoice.billNumber,
        billDate: normaliseInvoiceDate(extractedInvoice.billDate) || extractedInvoice.billDate
      }));
      setNewSupplierDraft(emptyNewSupplier);
    } else {
      setSupplierMode("__new_supplier__");
      setNewSupplierDraft((current) => ({
        ...current,
        name: extractedInvoice.supplierName,
        gstin: extractedInvoice.gstin
      }));
      setBillDraft((current) => ({
        ...current,
        supplierName: extractedInvoice.supplierName,
        supplierGstin: extractedInvoice.gstin,
        billNumber: extractedInvoice.billNumber,
        billDate: normaliseInvoiceDate(extractedInvoice.billDate) || extractedInvoice.billDate
      }));
    }
    setInvoiceLineDrafts(extractInvoiceLineCandidates(extractedText));
  };

  const readInvoicePhoto = async () => {
    if (!invoiceFile || !isInvoiceImage) {
      setOcrStatus("unsupported");
      setOcrError("OCR works for JPG/PNG invoice photos right now. For PDF, upload a clear invoice photo.");
      return;
    }

    let worker;
    setOcrStatus("running");
    setOcrProgress(0);
    setOcrError("");
    setExtractedText("");
    setExtractedInvoice(emptyExtractedInvoice);

    try {
      const { createWorker } = await import("tesseract.js");
      worker = await createWorker("eng", 1, {
        logger: (message) => {
          if (message.status === "recognizing text") {
            setOcrProgress(Math.round((message.progress || 0) * 100));
          }
        }
      });
      const result = await worker.recognize(invoiceFile);
      const text = result?.data?.text || "";
      const fields = extractInvoiceFields(text);
      setExtractedText(text);
      setExtractedInvoice(fields);
      setSupplierMatch(findSupplierMatch(fields));
      setInvoiceLineDrafts(extractInvoiceLineCandidates(text));
      setOcrProgress(100);
      setOcrStatus("done");
    } catch (error) {
      console.error("Purchase invoice OCR failed", error);
      setOcrError(error?.message || "Could not read this invoice. Try a clearer JPG/PNG photo.");
      setOcrStatus("error");
    } finally {
      if (worker) {
        await worker.terminate();
      }
    }
  };

  const viewOriginalInvoice = () => {
    if (!invoiceFile) return;
    const url = URL.createObjectURL(invoiceFile);
    window.open(url, "_blank", "noopener,noreferrer");
    window.setTimeout(() => URL.revokeObjectURL(url), 30000);
  };

  return (
    <div className="pv1-shell">
      <aside className="pv1-sidebar">
        <div className="pv1-brand">SparkingCircle</div>
        <button type="button">⌂ Home</button>
        <button type="button">◎ Attendance</button>
        <button type="button">✓ Tasks</button>
        <p>Inventory</p>
        <button type="button">▦ Products</button>
        <button type="button">◫ Stock</button>
        <button className="active" type="button">🧾 Purchase</button>
        <button type="button">↩ Purchase Return</button>
        <button type="button">⇄ Transfer</button>
        <button type="button">✓ Audit</button>
      </aside>

      <div className="pv1-app">
        <header className="pv1-topbar">
          <div className="pv1-topline">
            <button className="pv1-back" type="button">‹</button>
            <h1>Purchase</h1>
            <div className="pv1-profile">GM</div>
          </div>
          <nav className="pv1-tabs" aria-label="Purchase sections">
            {[
              ["overview", "Overview"],
              ["newPurchase", "New Purchase"],
              ["drafts", "Drafts"],
              ["history", "History"]
            ].map(([key, label]) => (
              <button key={key} className={view === key ? "active" : ""} type="button" onClick={() => setView(key)}>
                {label}
              </button>
            ))}
          </nav>
        </header>

        <main className="pv1-main">
          {view === "overview" ? (
            <section>
              <div className="pv1-stats">
                {overviewStats.map(([label, value, note]) => (
                  <div className="pv1-stat" key={label}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                    <small>{note}</small>
                  </div>
                ))}
              </div>
              <div className="pv1-layout">
                <div className="pv1-card">
                  <div className="pv1-card-title">
                    <h2>Recent purchases</h2>
                    <button type="button" onClick={() => setView("history")}>View all</button>
                  </div>
                  {recentPurchases.length ? recentPurchases.map((row) => <PurchaseRow key={row[1]} row={row} />) : <EmptyState title="No purchases yet" note="New purchases will appear here after they are saved." />}
                </div>
                <div>
                  <div className="pv1-card">
                    <div className="pv1-card-title"><h2>Needs attention</h2></div>
                    <EmptyState title="Nothing pending" note="Duplicate checks, payment reminders and OCR review items will appear here." />
                  </div>
                  <button className="pv1-primary desktop-only full" type="button" onClick={openPurchase}>+ New Purchase</button>
                </div>
              </div>
            </section>
          ) : null}

          {view === "newPurchase" ? (
            <section className="pv1-new-purchase">
              <div className="pv1-card pv1-ocr-card">
                <div className="pv1-card-title"><h2>Add supplier invoice</h2><Badge>OCR ready</Badge></div>
                <input
                  ref={uploadInvoiceInputRef}
                  className="pv1-file-input"
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  onChange={handleInvoiceFile}
                />
                <div className="pv1-ocr-actions">
                  <button type="button" disabled><i>📷</i>Scan invoice<span>Camera scan coming soon</span></button>
                  <button type="button" onClick={() => uploadInvoiceInputRef.current?.click()}><i>↑</i>Upload invoice<span>PDF, JPG or PNG</span></button>
                </div>
                <div className={`pv1-ocr-status ${invoiceFile ? "ready" : ""}`}>
                  <div>{invoiceFile ? "✓" : "!"}</div>
                  <p>
                    <strong>{invoiceStatusText}</strong>
                    <span>
                      {invoiceFile && isInvoiceImage ? "Invoice selected. Click Read invoice to extract data for review." : null}
                      {invoiceFile && isInvoicePdf ? "PDF selected. OCR extraction currently works from JPG/PNG photos." : null}
                      {!invoiceFile ? "Upload a PDF, JPG or PNG invoice. Nothing is saved automatically." : null}
                    </span>
                  </p>
                  <button type="button" disabled={!invoiceFile} onClick={viewOriginalInvoice}>View original invoice</button>
                </div>
                {invoiceFile ? (
                  <div className="pv1-invoice-preview">
                    {isInvoiceImage && invoicePreviewUrl ? <img src={invoicePreviewUrl} alt="Selected invoice preview" /> : null}
                    {isInvoicePdf ? (
                      <div className="pv1-pdf-preview">
                        <strong>PDF selected</strong>
                        <span>{invoiceFile.name}</span>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {invoiceFile ? (
                  <div className="pv1-ocr-read-panel">
                    <button className="pv1-ocr-read-button" type="button" disabled={!isInvoiceImage || ocrStatus === "running"} onClick={readInvoicePhoto}>
                      {ocrStatus === "running" ? "Reading invoice..." : "Read invoice"}
                    </button>
                    {ocrStatus === "running" ? (
                      <div className="pv1-ocr-progress" aria-label={`OCR progress ${ocrProgress}%`}>
                        <span style={{ width: `${ocrProgress}%` }} />
                      </div>
                    ) : null}
                    {ocrStatus === "unsupported" || ocrStatus === "error" ? <p className="pv1-ocr-error">{ocrError}</p> : null}
                    {isInvoicePdf ? <p className="pv1-ocr-note">For extraction now, upload a clear JPG/PNG invoice photo. PDF reading can be added later.</p> : null}
                  </div>
                ) : null}
                {ocrStatus === "done" ? (
                  <div className="pv1-ocr-review">
                    <div className="pv1-card-title">
                      <h2>Extracted invoice data</h2>
                      <Badge tone="blue">Review before use</Badge>
                    </div>
                    <p className="pv1-helper">OCR can make mistakes. Check and correct these fields before using them in the bill.</p>
                    <div className="pv1-field-grid cols2">
                      <Field label="Supplier name"><input value={extractedInvoice.supplierName} onChange={(event) => updateExtractedInvoice("supplierName", event.target.value)} /></Field>
                      <Field label="Bill number"><input value={extractedInvoice.billNumber} onChange={(event) => updateExtractedInvoice("billNumber", event.target.value)} /></Field>
                      <Field label="Bill date"><input value={extractedInvoice.billDate} onChange={(event) => updateExtractedInvoice("billDate", event.target.value)} /></Field>
                      <Field label="Supplier GSTIN"><input value={extractedInvoice.gstin} onChange={(event) => updateExtractedInvoice("gstin", event.target.value)} /></Field>
                      <Field label="Taxable amount"><input inputMode="decimal" value={extractedInvoice.taxableAmount} onChange={(event) => updateExtractedInvoice("taxableAmount", event.target.value)} /></Field>
                      <Field label="GST amount"><input inputMode="decimal" value={extractedInvoice.gstAmount} onChange={(event) => updateExtractedInvoice("gstAmount", event.target.value)} /></Field>
                      <Field label="Total amount"><input inputMode="decimal" value={extractedInvoice.totalAmount} onChange={(event) => updateExtractedInvoice("totalAmount", event.target.value)} /></Field>
                      <Field label="Reference / DC / DN / LRF"><input value={extractedInvoice.vendorReference} onChange={(event) => updateExtractedInvoice("vendorReference", event.target.value)} /></Field>
                    </div>
                    <Field label="Raw text read from invoice">
                      <textarea rows={6} value={extractedText} onChange={(event) => setExtractedText(event.target.value)} />
                    </Field>
                    <button className="pv1-primary full" type="button" onClick={applyExtractedInvoiceToDraft}>
                      Use extracted data in purchase draft
                    </button>
                    <div className="pv1-extract-result">
                      {supplierMatch ? (
                        <span>Matched supplier: <strong>{supplierMatch.name}</strong></span>
                      ) : (
                        <span>No exact supplier match yet. Supplier will open as a new supplier draft.</span>
                      )}
                      <span>{invoiceLineDrafts.length} possible product line{invoiceLineDrafts.length === 1 ? "" : "s"} found for review.</span>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="pv1-card pv1-bill-card">
                <div className="pv1-card-title"><h2>Bill details</h2><Badge tone="amber">Draft</Badge></div>
                <div className="pv1-field-grid cols2">
                  <Field label="Supplier *">
                    <select
                      value={supplierMode}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSupplierMode(value);
                        const selectedSupplier = knownSuppliers.find((supplier) => supplier.name === value);
                        setSupplierMatch(selectedSupplier || null);
                        if (selectedSupplier) {
                          setBillDraft((current) => ({ ...current, supplierName: selectedSupplier.name, supplierGstin: selectedSupplier.gstin }));
                        }
                      }}
                    >
                      <option value="">Search / select supplier</option>
                      {knownSuppliers.map((supplier) => <option key={supplier.name} value={supplier.name}>{supplier.name}</option>)}
                      <option value="__new_supplier__">+ Add new supplier</option>
                    </select>
                  </Field>
                  <Field label="Bill / challan number *"><input placeholder="Bill / challan number" value={billDraft.billNumber} onChange={(event) => updateBillDraft("billNumber", event.target.value)} /></Field>
                  <Field label="Bill date *"><input type="date" value={billDraft.billDate} onChange={(event) => updateBillDraft("billDate", event.target.value)} /></Field>
                  <Field label="Invoice type"><select value={billDraft.invoiceType} onChange={(event) => updateBillDraft("invoiceType", event.target.value)}><option value="">Select invoice type</option><option>Tax invoice</option><option>Bill of supply</option><option>Purchase challan</option><option>Debit note</option></select></Field>
                  <Field label="Supplier GSTIN"><input placeholder="GSTIN" value={billDraft.supplierGstin} onChange={(event) => updateBillDraft("supplierGstin", event.target.value)} /></Field>
                  <Field label="Place of supply"><select value={billDraft.placeOfSupply} onChange={(event) => updateBillDraft("placeOfSupply", event.target.value)}><option value="">Select state</option><option>Uttar Pradesh (09)</option><option>Delhi (07)</option><option>Haryana (06)</option><option>Maharashtra (27)</option><option>Other</option></select></Field>
                  <Field label="Price tax rule"><select value={billDraft.taxRule} onChange={(event) => updateBillDraft("taxRule", event.target.value)}><option value="">Select tax rule</option><option>Tax excluded</option><option>Tax included</option><option>Not applicable</option></select></Field>
                  <Field label="Default tax type"><select value={billDraft.taxType} onChange={(event) => updateBillDraft("taxType", event.target.value)}><option value="">Select tax type</option><option>Auto · CGST + SGST</option><option>IGST</option><option>Composition purchase</option><option>No GST</option></select></Field>
                  <Field label="Receiving business"><select value={billDraft.receivingBusiness} onChange={(event) => updateBillDraft("receivingBusiness", event.target.value)}><option value="">Select business</option></select></Field>
                  <Field label="Receiving store / location"><select value={billDraft.receivingStore} onChange={(event) => updateBillDraft("receivingStore", event.target.value)}><option value="">Select store</option><option>Sector 120</option><option>Sector 137</option><option>Crossing Republik</option><option>Paras Tierra</option><option>Head Office</option></select></Field>
                  <Field label="E-way bill number"><input placeholder="If applicable" value={billDraft.ewayBill} onChange={(event) => updateBillDraft("ewayBill", event.target.value)} /></Field>
                  <Field label="IRN / e-invoice"><input placeholder="Extracted from QR when available" value={billDraft.irn} onChange={(event) => updateBillDraft("irn", event.target.value)} /></Field>
                </div>
                {supplierMode === "__new_supplier__" ? (
                  <div className="pv1-inline-panel">
                    <div className="pv1-card-title">
                      <h2>Add new supplier</h2>
                      <Badge tone="blue">UI only</Badge>
                    </div>
                    <div className="pv1-field-grid cols2">
                      <Field label="Supplier name *"><input placeholder="Enter supplier name" value={newSupplierDraft.name} onChange={(event) => updateNewSupplierDraft("name", event.target.value)} autoFocus /></Field>
                      <Field label="Supplier GSTIN"><input placeholder="GSTIN if available" value={newSupplierDraft.gstin} onChange={(event) => updateNewSupplierDraft("gstin", event.target.value)} /></Field>
                      <Field label="Phone"><input inputMode="tel" placeholder="Supplier phone" value={newSupplierDraft.phone} onChange={(event) => updateNewSupplierDraft("phone", event.target.value)} /></Field>
                      <Field label="City"><input placeholder="City" value={newSupplierDraft.city} onChange={(event) => updateNewSupplierDraft("city", event.target.value)} /></Field>
                    </div>
                    <p className="pv1-helper">This only prepares the supplier on this screen. Real supplier saving will be connected later.</p>
                  </div>
                ) : null}
                <div className="pv1-warning"><b>!</b><span>Duplicate bill check will run after supplier and bill number are entered.</span></div>
              </div>

              <div className="pv1-card pv1-lines-card">
                <div className="pv1-card-title">
                  <h2>Invoice lines <span>{invoiceLineDrafts.length}</span></h2>
                  <button type="button" onClick={() => setSheetOpen(true)}>+ Add line</button>
                </div>
                <div className="mobile-only">
                  {!invoiceLineDrafts.length ? <EmptyState title="No invoice lines yet" note="Upload and read an invoice, add manually, or scan product barcode to start." /> : null}
                  {invoiceLineDrafts.map((item) => (
                    <article className={`pv1-item ${item.status === "approved" ? "approved" : ""}`} key={item.id}>
                      <div className="pv1-item-top">
                        <div className="pv1-item-icon">{item.status === "approved" ? "✓" : "?"}</div>
                        <div className="pv1-item-info">
                          <strong>{item.productType}</strong>
                          <span>Qty {item.qty || "-"} · Total ₹{item.total || "0"}</span>
                          <div className="pv1-line-tags"><span>{item.status === "approved" ? "Ready for inventory approval" : "Review needed"}</span></div>
                        </div>
                        <button type="button" onClick={() => removeInvoiceLineDraft(item.id)}>Remove</button>
                      </div>
                      <div className="pv1-line-edit-grid">
                        <Field label="Product details"><textarea rows={3} value={item.productDetails} onChange={(event) => updateInvoiceLineDraft(item.id, "productDetails", event.target.value)} /></Field>
                        <Field label="Product type"><select value={item.productType} onChange={(event) => updateInvoiceLineDraft(item.id, "productType", event.target.value)}><option>Frame</option><option>Lens</option><option>Sunglasses</option><option>Contact Lens</option><option>Solution</option><option>Accessory</option><option>Other</option></select></Field>
                        <Field label="HSN/SAC"><input value={item.hsn} onChange={(event) => updateInvoiceLineDraft(item.id, "hsn", event.target.value)} /></Field>
                        <Field label="Qty"><input inputMode="decimal" value={item.qty} onChange={(event) => updateInvoiceLineDraft(item.id, "qty", event.target.value)} /></Field>
                        <Field label="Unit rate"><input inputMode="decimal" value={item.rate} onChange={(event) => updateInvoiceLineDraft(item.id, "rate", event.target.value)} /></Field>
                        <Field label="Line total"><input inputMode="decimal" value={item.total} onChange={(event) => updateInvoiceLineDraft(item.id, "total", event.target.value)} /></Field>
                      </div>
                      <div className="pv1-line-actions">
                        <button type="button" onClick={() => approveInvoiceLineDraft(item.id)}>{item.status === "approved" ? "Approved" : "Approve line"}</button>
                        <button type="button" onClick={() => setSheetOpen(true)}>Open product detail</button>
                      </div>
                    </article>
                  ))}
                  <div className="pv1-actions-two">
                    <button type="button" onClick={() => setSheetOpen(true)}>＋ Add manually</button>
                    <button type="button">▦ Scan product</button>
                  </div>
                </div>
                <div className="desktop-only pv1-table-wrap">
                  {invoiceLineDrafts.length ? <table>
                    <thead>
                      <tr>
                        <th>#</th><th>Product type</th><th>Product code</th><th>Details</th><th>HSN/SAC</th><th>Qty</th><th>Unit price</th><th>Disc</th><th>GST</th><th>Total</th><th>Retail</th><th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceLineDrafts.map((item, index) => (
                        <tr key={item.id} className={item.status === "approved" ? "pv1-approved-row" : ""}>
                          <td>{index + 1}</td>
                          <td><select value={item.productType} onChange={(event) => updateInvoiceLineDraft(item.id, "productType", event.target.value)}><option>Frame</option><option>Lens</option><option>Sunglasses</option><option>Contact Lens</option><option>Solution</option><option>Accessory</option><option>Other</option></select></td>
                          <td>-</td>
                          <td><textarea rows={2} value={item.productDetails} onChange={(event) => updateInvoiceLineDraft(item.id, "productDetails", event.target.value)} /></td>
                          <td><input value={item.hsn} onChange={(event) => updateInvoiceLineDraft(item.id, "hsn", event.target.value)} /></td>
                          <td><input value={item.qty} onChange={(event) => updateInvoiceLineDraft(item.id, "qty", event.target.value)} /></td>
                          <td><input value={item.rate} onChange={(event) => updateInvoiceLineDraft(item.id, "rate", event.target.value)} /></td>
                          <td>-</td><td>-</td>
                          <td><input value={item.total} onChange={(event) => updateInvoiceLineDraft(item.id, "total", event.target.value)} /></td>
                          <td>-</td>
                          <td><button type="button" onClick={() => approveInvoiceLineDraft(item.id)}>{item.status === "approved" ? "Approved" : "Approve"}</button><button type="button" onClick={() => removeInvoiceLineDraft(item.id)}>Remove</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table> : <EmptyState title="No invoice lines yet" note="Upload and read an invoice, add manually, or scan product barcode to start." />}
                  <div className="pv1-table-actions">
                    <button type="button" onClick={() => setSheetOpen(true)}>＋ Add row</button>
                    <button type="button">▦ Scan products</button>
                    <button type="button">% Bill discount</button>
                    <span>Press Tab to move across fields · Enter to add another row</span>
                  </div>
                </div>
              </div>

              <div className="pv1-card pv1-payment-card">
                <div className="pv1-card-title"><h2>Payment</h2></div>
                <div className="pv1-segmented">
                  {["Credit", "Paid", "Part paid"].map((item) => <button key={item} className={payment === item ? "active" : ""} type="button" onClick={() => setPayment(item)}>{item}</button>)}
                </div>
                <div className="pv1-field-grid cols2">
                  <Field label="Credit period"><select defaultValue=""><option value="">Select period</option><option>30 days</option><option>15 days</option><option>45 days</option><option>60 days</option></select></Field>
                  <Field label="Due date"><input type="date" /></Field>
                  <Field label="Paid now"><input placeholder="0" /></Field>
                  <Field label="Payment reference"><input placeholder="Optional" /></Field>
                </div>
              </div>

              <div className="pv1-card pv1-tax-card">
                <div className="pv1-card-title"><h2>Tax treatment</h2><Badge>Intra-state</Badge></div>
                <div className="pv1-field-grid cols2">
                  <Field label="Tax mode"><select defaultValue=""><option value="">Select tax mode</option><option>CGST + SGST</option><option>IGST</option><option>No GST</option></select></Field>
                  <Field label="Input-tax credit"><select defaultValue=""><option value="">Select ITC</option><option>Eligible</option><option>Partly eligible</option><option>Ineligible / blocked</option><option>Pending verification</option></select></Field>
                  <Field label="Reverse charge"><select defaultValue=""><option value="">Select</option><option>No</option><option>Yes — RCM</option></select></Field>
                  <Field label="GSTR-2B status"><select defaultValue=""><option value="">Select status</option><option>Not checked</option><option>Matched</option><option>Mismatch</option><option>Not appearing</option></select></Field>
                </div>
              </div>

              <div className="pv1-card pv1-total-card">
                <div className="pv1-card-title"><h2>Bill total</h2></div>
                {[
                  ["Total quantity", String(draftQuantity)],
                  ["Gross line value", `₹${draftGrandTotal}`],
                  ["Discount", "− ₹0"],
                  ["Taxable value", `₹${draftTaxableAmount}`],
                  ["GST extracted", `₹${draftGstAmount}`],
                  ["IGST", "₹0"],
                  ["Other charges", "₹0"],
                  ["Round off", "+ ₹0"]
                ].map(([label, value]) => <div className="pv1-total-row" key={label}><span>{label}</span><strong>{value}</strong></div>)}
                <div className="pv1-total-row grand"><span>Grand total</span><strong>₹{draftGrandTotal}</strong></div>
                <button type="button">Edit discount, tax or charges</button>
              </div>

              <div className="pv1-card pv1-posting-card">
                <div className="pv1-card-title"><h2>Posting summary</h2></div>
                {[
                  ["Supplier and bill", billDraft.supplierName || newSupplierDraft.name ? `Supplier draft ready: ${billDraft.supplierName || newSupplierDraft.name}` : "Pending supplier and bill details"],
                  ["Inventory", invoiceLineDrafts.length ? `${approvedLineCount}/${invoiceLineDrafts.length} lines approved. Stock will still need final posting later.` : "No stock will be added until invoice lines are entered"],
                  ["Service expense", "No service expense entered"],
                  ["Input GST", draftGstAmount !== "0" ? `GST extracted for review: ₹${draftGstAmount}` : "GST will calculate after HSN/SAC and tax values are entered"],
                  ["Supplier payable", draftGrandTotal !== "0" ? `Draft payable for review: ₹${draftGrandTotal}` : "Payable will calculate after bill total is available"]
                ].map(([title, text]) => (
                  <div className="pv1-review" key={title}><div>✓</div><p><strong>{title}</strong><span>{text}</span></p></div>
                ))}
              </div>

              <div className="pv1-actions-two pv1-post-actions desktop-only">
                <button type="button">Save Draft</button>
                <button className="pv1-primary" type="button">Review & Post</button>
              </div>
            </section>
          ) : null}

          {view === "drafts" ? (
            <section>
              <div className="pv1-search"><span>⌕</span><input placeholder="Search supplier or bill number" /></div>
              <div className="pv1-card">{drafts.length ? drafts.map((row) => <PurchaseRow key={row[1]} row={row} />) : <EmptyState title="No drafts yet" note="Saved draft purchases will appear here." />}</div>
            </section>
          ) : null}

          {view === "history" ? (
            <section>
              <div className="pv1-search"><span>⌕</span><input placeholder="Search supplier, bill, product or barcode" /><button type="button">Filter</button></div>
              <div className="pv1-card">{history.length ? history.map((row) => <PurchaseRow key={row[1]} row={row} />) : <EmptyState title="No purchase history yet" note="Posted purchases will appear here." />}</div>
            </section>
          ) : null}
        </main>

        <div className="pv1-bottom-bar">
          {view === "newPurchase" ? <button type="button">Save Draft</button> : null}
          <button className="pv1-primary" type="button" onClick={view === "newPurchase" ? undefined : openPurchase}>
            {view === "newPurchase" ? "Review & Post" : "+ New Purchase"}
          </button>
        </div>
      </div>

      <ProductDetailSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </div>
  );
}
